import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase, MEDIA_BUCKET } from './supabaseClient';
import {
  getAnalyticsOverview,
  getRecentSessions,
  getPageViewsByPage,
  getVisitorsByCountry,
  getVisitorsByDevice,
  getViewsByDay,
  getLiveVisitors
} from './analyticsService';
import {
  getSEOOverview,
  getSearchQueries,
  getCampaignPerformance,
  getWebVitalsHistory
} from './seoAnalyticsService';
import {
  getBodyScanSessions,
  getBodyScanOverview,
  getTechniqueEffectiveness,
  getZoneTensionData,
  getTriggerContexts,
  getSessionsByDay,
  getFavoritesOverview,
  getTopFavoriteTechniques,
  getUserFeedback,
  getFeedbackOverview,
  resolveFeedback
} from './bodyScanService';
import './AdminPanel.css';

const AdminPanel = () => {
  const { tab } = useParams();
  const navigate = useNavigate();
  const activeTab = tab || 'analytics';

  const setActiveTab = (newTab) => {
    navigate(`/admin/${newTab}`);
  };
  const [blogs, setBlogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    content: '',
    slug: '',
  });
  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Analytics state
  const [analyticsData, setAnalyticsData] = useState({
    overview: null,
    recentSessions: [],
    pageViews: [],
    countries: [],
    devices: { devices: [], browsers: [], os: [] },
    viewsByDay: [],
    liveVisitors: []
  });
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [dateRange, setDateRange] = useState(30);

  // SEO Analytics state
  const [seoData, setSeoData] = useState({
    overview: null,
    searchQueries: [],
    campaigns: [],
    webVitalsHistory: []
  });
  const [seoLoading, setSeoLoading] = useState(true);

  // Body Scan state
  const [bodyScanData, setBodyScanData] = useState({
    overview: null,
    sessions: [],
    techniques: [],
    zones: [],
    triggers: [],
    sessionsByDay: [],
    favoritesOverview: null,
    topFavorites: []
  });
  const [bodyScanLoading, setBodyScanLoading] = useState(true);

  // Feedback state
  const [feedbackData, setFeedbackData] = useState({
    overview: null,
    feedbackList: []
  });
  const [feedbackLoading, setFeedbackLoading] = useState(true);
  const [selectedTechnique, setSelectedTechnique] = useState(null);

  useEffect(() => {
    if (activeTab === 'blogs') {
      fetchBlogs();
    } else if (activeTab === 'analytics') {
      fetchAnalytics();
    } else if (activeTab === 'seo') {
      fetchSEOAnalytics();
    } else if (activeTab === 'bodyscan') {
      fetchBodyScanData();
    } else if (activeTab === 'feedback') {
      fetchFeedbackData();
    }
  }, [tab, dateRange]);

  // Auto-refresh live visitors every 30 seconds
  useEffect(() => {
    if (activeTab === 'analytics') {
      const interval = setInterval(() => {
        fetchLiveVisitors();
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [tab]);

  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const [overview, recentSessions, pageViews, countries, devices, viewsByDay, liveVisitors] = await Promise.all([
        getAnalyticsOverview(dateRange),
        getRecentSessions(50),
        getPageViewsByPage(dateRange),
        getVisitorsByCountry(dateRange),
        getVisitorsByDevice(dateRange),
        getViewsByDay(dateRange),
        getLiveVisitors()
      ]);

      setAnalyticsData({
        overview,
        recentSessions,
        pageViews,
        countries,
        devices,
        viewsByDay,
        liveVisitors
      });
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const fetchLiveVisitors = async () => {
    const liveVisitors = await getLiveVisitors();
    setAnalyticsData(prev => ({ ...prev, liveVisitors }));
  };

  const fetchSEOAnalytics = async () => {
    setSeoLoading(true);
    try {
      const [overview, searchQueries, campaigns, webVitalsHistory] = await Promise.all([
        getSEOOverview(dateRange),
        getSearchQueries(dateRange),
        getCampaignPerformance(dateRange),
        getWebVitalsHistory(dateRange)
      ]);

      setSeoData({
        overview,
        searchQueries,
        campaigns,
        webVitalsHistory
      });
    } catch (error) {
      console.error('Error fetching SEO analytics:', error);
    } finally {
      setSeoLoading(false);
    }
  };

  const fetchBodyScanData = async () => {
    setBodyScanLoading(true);
    try {
      const [overview, sessions, techniques, zones, triggers, sessionsByDay, favoritesOverview, topFavorites] = await Promise.all([
        getBodyScanOverview(dateRange),
        getBodyScanSessions(dateRange, 50),
        getTechniqueEffectiveness(dateRange),
        getZoneTensionData(dateRange),
        getTriggerContexts(dateRange),
        getSessionsByDay(dateRange),
        getFavoritesOverview(),
        getTopFavoriteTechniques(10)
      ]);

      setBodyScanData({
        overview,
        sessions,
        techniques,
        zones,
        triggers,
        sessionsByDay,
        favoritesOverview,
        topFavorites
      });
    } catch (error) {
      console.error('Error fetching body scan data:', error);
    } finally {
      setBodyScanLoading(false);
    }
  };

  const fetchFeedbackData = async () => {
    setFeedbackLoading(true);
    try {
      const [overview, feedbackList] = await Promise.all([
        getFeedbackOverview(dateRange),
        getUserFeedback(dateRange, 100)
      ]);

      setFeedbackData({
        overview,
        feedbackList
      });
    } catch (error) {
      console.error('Error fetching feedback data:', error);
    } finally {
      setFeedbackLoading(false);
    }
  };

  const handleResolveFeedback = async (feedbackId) => {
    const result = await resolveFeedback(feedbackId);
    if (result) {
      fetchFeedbackData();
    }
  };

  const fetchBlogs = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('blogs')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setBlogs(data || []);
    } catch (error) {
      console.error('Error fetching blogs:', error);
      alert('Error fetching blogs: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleImageUpload = async (file) => {
    if (!file) return null;

    try {
      setUploading(true);
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}-${Date.now()}.${fileExt}`;
      const filePath = `blog-images/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from(MEDIA_BUCKET)
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from(MEDIA_BUCKET)
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Error uploading image: ' + error.message);
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      let imageUrl = formData.image_url;

      if (imageFile) {
        imageUrl = await handleImageUpload(imageFile);
        if (!imageUrl) return;
      }

      const slug = formData.slug || generateSlug(formData.title);

      const blogData = {
        title: formData.title,
        description: formData.description,
        image_url: imageUrl,
        content: formData.content,
        slug: slug,
      };

      if (editingBlog) {
        const { error } = await supabase
          .from('blogs')
          .update(blogData)
          .eq('id', editingBlog.id);

        if (error) throw error;
        alert('Blog updated successfully!');
      } else {
        const { error } = await supabase
          .from('blogs')
          .insert([blogData]);

        if (error) throw error;
        alert('Blog created successfully!');
      }

      resetForm();
      fetchBlogs();
    } catch (error) {
      console.error('Error saving blog:', error);
      alert('Error saving blog: ' + error.message);
    }
  };

  const handleEdit = (blog) => {
    setEditingBlog(blog);
    setFormData({
      title: blog.title,
      description: blog.description,
      image_url: blog.image_url,
      content: blog.content || '',
      slug: blog.slug || '',
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this blog?')) return;

    try {
      const { error } = await supabase
        .from('blogs')
        .delete()
        .eq('id', id);

      if (error) throw error;
      alert('Blog deleted successfully!');
      fetchBlogs();
    } catch (error) {
      console.error('Error deleting blog:', error);
      alert('Error deleting blog: ' + error.message);
    }
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', image_url: '', content: '', slug: '' });
    setImageFile(null);
    setEditingBlog(null);
    setShowForm(false);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, image_url: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 0) {
      return `${mins}m ${secs}s`;
    }
    return `${secs}s`;
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);

    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  const getDeviceIcon = (type) => {
    switch (type) {
      case 'mobile': return '📱';
      case 'tablet': return '📲';
      default: return '💻';
    }
  };

  const renderAnalyticsDashboard = () => {
    const { overview, recentSessions, pageViews, countries, devices, viewsByDay, liveVisitors } = analyticsData;

    if (analyticsLoading) {
      return <div className="loading">Loading analytics...</div>;
    }

    return (
      <div className="analytics-dashboard">
        {/* Date Range Selector */}
        <div className="date-range-selector">
          <span className="date-range-label">Time period:</span>
          <div className="date-range-buttons">
            {[7, 14, 30, 90].map(days => (
              <button
                key={days}
                className={`date-range-btn ${dateRange === days ? 'active' : ''}`}
                onClick={() => setDateRange(days)}
              >
                {days} days
              </button>
            ))}
          </div>
        </div>

        {/* Live Visitors */}
        {liveVisitors.length > 0 && (
          <div className="live-visitors-banner">
            <span className="live-dot"></span>
            <span className="live-count">{liveVisitors.length}</span>
            <span className="live-text">visitor{liveVisitors.length !== 1 ? 's' : ''} online now</span>
          </div>
        )}

        {/* Overview Cards */}
        <div className="stats-overview">
          <div className="stat-card">
            <div className="stat-icon">👁️</div>
            <div className="stat-content">
              <div className="stat-value">{overview?.totalPageViews?.toLocaleString() || 0}</div>
              <div className="stat-label">Page Views</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">👤</div>
            <div className="stat-content">
              <div className="stat-value">{overview?.uniqueVisitors?.toLocaleString() || 0}</div>
              <div className="stat-label">Unique Visitors</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🔄</div>
            <div className="stat-content">
              <div className="stat-value">{overview?.totalSessions?.toLocaleString() || 0}</div>
              <div className="stat-label">Sessions</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">⏱️</div>
            <div className="stat-content">
              <div className="stat-value">{formatDuration(overview?.avgSessionDuration)}</div>
              <div className="stat-label">Avg. Session</div>
            </div>
          </div>
        </div>

        {/* Charts Section */}
        <div className="analytics-grid">
          {/* Views Over Time */}
          <div className="analytics-card chart-card">
            <h3 className="card-title">Views Over Time</h3>
            <div className="mini-chart">
              {viewsByDay.length > 0 && (
                <div className="bar-chart">
                  {viewsByDay.map((day, i) => {
                    const maxViews = Math.max(...viewsByDay.map(d => d.views), 1);
                    const height = (day.views / maxViews) * 100;
                    return (
                      <div key={i} className="bar-container" title={`${day.date}: ${day.views} views`}>
                        <div className="bar" style={{ height: `${Math.max(height, 2)}%` }}></div>
                        {i % 7 === 0 && <span className="bar-label">{new Date(day.date).getDate()}</span>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Top Pages */}
          <div className="analytics-card">
            <h3 className="card-title">Top Pages</h3>
            <div className="list-container">
              {pageViews.slice(0, 5).map((page, i) => (
                <div key={i} className="list-item">
                  <span className="list-rank">{i + 1}</span>
                  <span className="list-name">{page.page || '/'}</span>
                  <span className="list-value">{page.views}</span>
                </div>
              ))}
              {pageViews.length === 0 && <div className="empty-list">No page views yet</div>}
            </div>
          </div>

          {/* Countries */}
          <div className="analytics-card">
            <h3 className="card-title">Top Countries</h3>
            <div className="list-container">
              {countries.slice(0, 5).map((country, i) => (
                <div key={i} className="list-item">
                  <span className="list-flag">{country.code ? getFlagEmoji(country.code) : '🌍'}</span>
                  <span className="list-name">{country.country}</span>
                  <span className="list-value">{country.count}</span>
                </div>
              ))}
              {countries.length === 0 && <div className="empty-list">No location data yet</div>}
            </div>
          </div>

          {/* Devices */}
          <div className="analytics-card">
            <h3 className="card-title">Devices</h3>
            <div className="device-breakdown">
              {devices.devices.map((device, i) => {
                const total = devices.devices.reduce((acc, d) => acc + d.count, 0) || 1;
                const percentage = Math.round((device.count / total) * 100);
                return (
                  <div key={i} className="device-item">
                    <span className="device-icon">{getDeviceIcon(device.name)}</span>
                    <span className="device-name">{device.name}</span>
                    <div className="device-bar-container">
                      <div className="device-bar" style={{ width: `${percentage}%` }}></div>
                    </div>
                    <span className="device-percent">{percentage}%</span>
                  </div>
                );
              })}
              {devices.devices.length === 0 && <div className="empty-list">No device data yet</div>}
            </div>
          </div>

          {/* Browsers */}
          <div className="analytics-card">
            <h3 className="card-title">Browsers</h3>
            <div className="list-container">
              {devices.browsers.slice(0, 5).map((browser, i) => (
                <div key={i} className="list-item">
                  <span className="list-name">{browser.name}</span>
                  <span className="list-value">{browser.count}</span>
                </div>
              ))}
              {devices.browsers.length === 0 && <div className="empty-list">No browser data yet</div>}
            </div>
          </div>

          {/* Operating Systems */}
          <div className="analytics-card">
            <h3 className="card-title">Operating Systems</h3>
            <div className="list-container">
              {devices.os.slice(0, 5).map((os, i) => (
                <div key={i} className="list-item">
                  <span className="list-name">{os.name}</span>
                  <span className="list-value">{os.count}</span>
                </div>
              ))}
              {devices.os.length === 0 && <div className="empty-list">No OS data yet</div>}
            </div>
          </div>
        </div>

        {/* Recent Sessions */}
        <div className="analytics-card sessions-card">
          <h3 className="card-title">Recent Sessions</h3>
          <div className="sessions-table-container">
            <table className="sessions-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Location</th>
                  <th>Device</th>
                  <th>Browser</th>
                  <th>Duration</th>
                  <th>Pages</th>
                  <th>Landing Page</th>
                </tr>
              </thead>
              <tbody>
                {recentSessions.slice(0, 20).map((session, i) => (
                  <tr key={i}>
                    <td className="session-time">{formatTimeAgo(session.started_at)}</td>
                    <td className="session-location">
                      {session.country_code && <span className="flag">{getFlagEmoji(session.country_code)}</span>}
                      {session.city ? `${session.city}, ${session.country}` : session.country || 'Unknown'}
                    </td>
                    <td>{getDeviceIcon(session.device_type)} {session.device_type}</td>
                    <td>{session.browser}</td>
                    <td>{formatDuration(session.duration_seconds)}</td>
                    <td>{session.pages_viewed || 1}</td>
                    <td className="session-page">{session.landing_page}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {recentSessions.length === 0 && (
              <div className="empty-table">No sessions recorded yet</div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const getFlagEmoji = (countryCode) => {
    if (!countryCode) return '🌍';
    const codePoints = countryCode
      .toUpperCase()
      .split('')
      .map(char => 127397 + char.charCodeAt());
    return String.fromCodePoint(...codePoints);
  };

  const getWebVitalStatus = (name, value) => {
    const thresholds = {
      LCP: { good: 2500, poor: 4000 },
      FID: { good: 100, poor: 300 },
      CLS: { good: 0.1, poor: 0.25 },
      FCP: { good: 1800, poor: 3000 },
      TTFB: { good: 800, poor: 1800 }
    };
    const t = thresholds[name];
    if (!t) return 'neutral';
    if (value <= t.good) return 'good';
    if (value <= t.poor) return 'needs-improvement';
    return 'poor';
  };

  const renderSEODashboard = () => {
    const { overview, searchQueries, campaigns, webVitalsHistory } = seoData;

    if (seoLoading) {
      return <div className="loading">Loading SEO analytics...</div>;
    }

    return (
      <div className="seo-dashboard">
        {/* Date Range Selector */}
        <div className="date-range-selector">
          <span className="date-range-label">Time period:</span>
          <div className="date-range-buttons">
            {[7, 14, 30, 90].map(days => (
              <button
                key={days}
                className={`date-range-btn ${dateRange === days ? 'active' : ''}`}
                onClick={() => setDateRange(days)}
              >
                {days} days
              </button>
            ))}
          </div>
        </div>

        {/* SEO Overview Cards */}
        <div className="stats-overview">
          <div className="stat-card">
            <div className="stat-icon">🔍</div>
            <div className="stat-content">
              <div className="stat-value">{overview?.totalOrganicVisits || 0}</div>
              <div className="stat-label">Organic Visits</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📄</div>
            <div className="stat-content">
              <div className="stat-value">{overview?.topPages?.length || 0}</div>
              <div className="stat-label">Indexed Pages</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">🎯</div>
            <div className="stat-content">
              <div className="stat-value">{campaigns?.length || 0}</div>
              <div className="stat-label">Active Campaigns</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">⚡</div>
            <div className="stat-content">
              <div className="stat-value">{overview?.webVitals?.LCP ? `${(overview.webVitals.LCP / 1000).toFixed(1)}s` : 'N/A'}</div>
              <div className="stat-label">Avg. LCP</div>
            </div>
          </div>
        </div>

        {/* SEO Analytics Grid */}
        <div className="analytics-grid">
          {/* Core Web Vitals */}
          <div className="analytics-card">
            <h3 className="card-title">Core Web Vitals</h3>
            <div className="web-vitals-list">
              {['LCP', 'FID', 'CLS', 'FCP', 'TTFB'].map(metric => {
                const value = overview?.webVitals?.[metric];
                const status = value ? getWebVitalStatus(metric, value) : 'neutral';
                return (
                  <div key={metric} className={`vital-item ${status}`}>
                    <span className="vital-name">{metric}</span>
                    <span className="vital-value">
                      {value ? (metric === 'CLS' ? value.toFixed(3) : `${value}ms`) : 'No data'}
                    </span>
                    <span className={`vital-status ${status}`}>
                      {status === 'good' ? '✓' : status === 'poor' ? '✗' : '–'}
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="vital-legend">
              <span className="legend-item good">Good</span>
              <span className="legend-item needs-improvement">Needs Work</span>
              <span className="legend-item poor">Poor</span>
            </div>
          </div>

          {/* Search Engines */}
          <div className="analytics-card">
            <h3 className="card-title">Traffic Sources</h3>
            <div className="list-container">
              {Object.entries(overview?.searchEngines || {}).map(([engine, count], i) => (
                <div key={i} className="list-item">
                  <span className="list-name">{engine}</span>
                  <span className="list-value">{count}</span>
                </div>
              ))}
              {Object.keys(overview?.searchEngines || {}).length === 0 && (
                <div className="empty-list">No search engine data yet</div>
              )}
            </div>
          </div>

          {/* Top Pages */}
          <div className="analytics-card">
            <h3 className="card-title">Top Performing Pages</h3>
            <div className="list-container">
              {(overview?.topPages || []).slice(0, 5).map((page, i) => (
                <div key={i} className="list-item">
                  <span className="list-rank">{i + 1}</span>
                  <span className="list-name">{page.url || '/'}</span>
                  <span className="list-value">{page.views}</span>
                </div>
              ))}
              {(overview?.topPages || []).length === 0 && (
                <div className="empty-list">No page data yet</div>
              )}
            </div>
          </div>

          {/* UTM Campaigns */}
          <div className="analytics-card">
            <h3 className="card-title">Campaign Performance</h3>
            <div className="list-container">
              {(campaigns || []).slice(0, 5).map((campaign, i) => (
                <div key={i} className="list-item campaign-item">
                  <div className="campaign-info">
                    <span className="campaign-source">{campaign.source}</span>
                    <span className="campaign-medium">/ {campaign.medium}</span>
                  </div>
                  <span className="list-value">{campaign.visits}</span>
                </div>
              ))}
              {(campaigns || []).length === 0 && (
                <div className="empty-list">No campaign data yet. Add UTM parameters to your links.</div>
              )}
            </div>
          </div>
        </div>

        {/* Search Queries */}
        <div className="analytics-card sessions-card">
          <h3 className="card-title">Recent Search Queries</h3>
          <div className="sessions-table-container">
            <table className="sessions-table">
              <thead>
                <tr>
                  <th>Query</th>
                  <th>Source</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {(searchQueries || []).slice(0, 15).map((query, i) => (
                  <tr key={i}>
                    <td>{query.search_query || '(not provided)'}</td>
                    <td>{query.search_engine}</td>
                    <td>{formatTimeAgo(query.timestamp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(searchQueries || []).length === 0 && (
              <div className="empty-table">No search queries recorded yet</div>
            )}
          </div>
        </div>

        {/* SEO Tips */}
        <div className="analytics-card seo-tips-card">
          <h3 className="card-title">SEO Checklist</h3>
          <div className="seo-checklist">
            <div className="checklist-item done">
              <span className="check-icon">✓</span>
              <span>Meta tags & Open Graph configured</span>
            </div>
            <div className="checklist-item done">
              <span className="check-icon">✓</span>
              <span>Structured data (JSON-LD) added</span>
            </div>
            <div className="checklist-item done">
              <span className="check-icon">✓</span>
              <span>Sitemap.xml created</span>
            </div>
            <div className="checklist-item done">
              <span className="check-icon">✓</span>
              <span>Robots.txt configured</span>
            </div>
            <div className="checklist-item">
              <span className="check-icon">○</span>
              <span>Submit sitemap to Google Search Console</span>
            </div>
            <div className="checklist-item">
              <span className="check-icon">○</span>
              <span>Submit sitemap to Bing Webmaster Tools</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const formatZoneName = (zoneKey) => {
    const zoneNames = {
      'head': 'Tête',
      'neck': 'Cou',
      'shoulders': 'Épaules',
      'upper_back': 'Haut du dos',
      'lower_back': 'Bas du dos',
      'chest': 'Poitrine',
      'stomach': 'Ventre',
      'arms': 'Bras',
      'hands': 'Mains',
      'hips': 'Hanches',
      'legs': 'Jambes',
      'feet': 'Pieds'
    };
    return zoneNames[zoneKey] || zoneKey;
  };

  // Technique ID to name mapping
  const techniqueNames = {
    // Main techniques (format: a1000001-0001-0001-0001-00000000000X)
    'a1000001-0001-0001-0001-000000000001': 'Respiration Profonde',
    'a1000001-0001-0001-0001-000000000002': 'Respiration Carrée',
    'a1000001-0001-0001-0001-000000000003': 'Cohérence Cardiaque',
    'a1000001-0001-0001-0001-000000000004': 'Scan Corporel',
    'a1000001-0001-0001-0001-000000000005': 'Écoute du Coeur',
    'a1000001-0001-0001-0001-000000000006': 'Ancrage',
    'a1000001-0001-0001-0001-000000000007': 'Relaxation Musculaire',
    'a1000001-0001-0001-0001-000000000008': 'Visualisation',
    // Alternative format (aX000000X-000X...)
    'a2000002-0002-0002-0002-000000000001': 'Respiration Carrée',
    'a3000003-0003-0003-0003-000000000001': 'Cohérence Cardiaque',
    'a4000004-0004-0004-0004-000000000001': 'Scan Corporel',
    'a5000005-0005-0005-0005-000000000001': 'Écoute du Coeur',
    'a6000006-0006-0006-0006-000000000001': 'Ancrage',
    'a7000007-0007-0007-0007-000000000001': 'Relaxation Musculaire',
    'a8000008-0008-0008-0008-000000000001': 'Visualisation',
    // Quick techniques
    'quick_breathing': 'Respiration Rapide',
    'quick_shoulders': 'Épaules Rapide',
    'quick_grounding': 'Ancrage Rapide',
    'quick_relaxation': 'Relaxation Rapide',
    // Observation techniques
    'observe_thoughts': 'Observer ses Pensées',
    'observe_emotions': 'Observer ses Émotions',
    'observe_sensations': 'Observer ses Sensations'
  };

  const getTechniqueName = (id) => {
    if (!id) return 'Non spécifié';
    if (techniqueNames[id]) return techniqueNames[id];
    // Try to extract a meaningful name from the ID
    const cleanId = id.replace(/_/g, ' ').replace(/-/g, ' ');
    return cleanId.replace(/^\w/, c => c.toUpperCase());
  };

  // Trigger context to readable name mapping
  const formatTriggerContext = (context) => {
    if (!context) return 'Non spécifié';

    // Check if it's a library technique reference
    if (context.startsWith('library_')) {
      const techniqueId = context.replace('library_', '');
      const techniqueName = techniqueNames[techniqueId];
      return techniqueName ? `Bibliothèque: ${techniqueName}` : 'Bibliothèque';
    }

    // Map known contexts to French labels
    const contextNames = {
      'manual': 'Manuel',
      'observe_thoughts': 'Observer ses pensées',
      'observe_emotions': 'Observer ses émotions',
      'observe_sensations': 'Observer ses sensations',
      'library': 'Bibliothèque',
      'quick_breathing': 'Respiration rapide',
      'quick_shoulders': 'Épaules rapide',
      'quick_grounding': 'Ancrage rapide',
      'quick_relaxation': 'Relaxation rapide',
      'morning_routine': 'Routine matinale',
      'evening_routine': 'Routine du soir',
      'stress_relief': 'Soulagement du stress',
      'anxiety_relief': 'Soulagement de l\'anxiété',
      'focus': 'Concentration',
      'sleep': 'Sommeil',
      'energy': 'Énergie',
      'calm': 'Calme',
      'notification': 'Notification',
      'widget': 'Widget',
      'shortcut': 'Raccourci'
    };

    return contextNames[context] || context.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());
  };

  const renderBodyScanDashboard = () => {
    const { overview, sessions, techniques, zones, triggers, sessionsByDay, favoritesOverview, topFavorites } = bodyScanData;

    if (bodyScanLoading) {
      return <div className="loading">Loading body scan data...</div>;
    }

    return (
      <div className="bodyscan-dashboard">
        {/* Date Range Selector */}
        <div className="date-range-selector">
          <span className="date-range-label">Time period:</span>
          <div className="date-range-buttons">
            {[7, 14, 30, 90].map(days => (
              <button
                key={days}
                className={`date-range-btn ${dateRange === days ? 'active' : ''}`}
                onClick={() => setDateRange(days)}
              >
                {days} days
              </button>
            ))}
          </div>
        </div>

        {/* Overview Cards */}
        <div className="stats-overview">
          <div className="stat-card">
            <div className="stat-icon">🧘</div>
            <div className="stat-content">
              <div className="stat-value">{overview?.totalSessions || 0}</div>
              <div className="stat-label">Total Sessions</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">✅</div>
            <div className="stat-content">
              <div className="stat-value">{overview?.completionRate || 0}%</div>
              <div className="stat-label">Completion Rate</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">📉</div>
            <div className="stat-content">
              <div className="stat-value">-{overview?.avgTensionReduction || 0}</div>
              <div className="stat-label">Avg Tension Reduction</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">⭐</div>
            <div className="stat-content">
              <div className="stat-value">{overview?.avgRating || 0}/5</div>
              <div className="stat-label">Avg Rating</div>
            </div>
          </div>
        </div>

        {/* Secondary Stats */}
        <div className="stats-overview secondary">
          <div className="stat-card small">
            <div className="stat-content">
              <div className="stat-value">{overview?.uniqueUsers || 0}</div>
              <div className="stat-label">Unique Users</div>
            </div>
          </div>
          <div className="stat-card small">
            <div className="stat-content">
              <div className="stat-value">{overview?.completedSessions || 0}</div>
              <div className="stat-label">Completed</div>
            </div>
          </div>
          <div className="stat-card small">
            <div className="stat-content">
              <div className="stat-value">{Math.floor((overview?.avgDuration || 0) / 60)}m {(overview?.avgDuration || 0) % 60}s</div>
              <div className="stat-label">Avg Duration</div>
            </div>
          </div>
        </div>

        {/* Analytics Grid */}
        <div className="analytics-grid">
          {/* Sessions Over Time */}
          <div className="analytics-card chart-card">
            <h3 className="card-title">Sessions Over Time</h3>
            <div className="mini-chart">
              {sessionsByDay.length > 0 && (
                <div className="bar-chart">
                  {sessionsByDay.map((day, i) => {
                    const maxSessions = Math.max(...sessionsByDay.map(d => d.total), 1);
                    const height = (day.total / maxSessions) * 100;
                    const completionHeight = day.total > 0 ? (day.completed / day.total) * height : 0;
                    return (
                      <div key={i} className="bar-container" title={`${day.date}: ${day.total} sessions (${day.completed} completed)`}>
                        <div className="bar stacked">
                          <div className="bar-segment completed" style={{ height: `${completionHeight}%` }}></div>
                          <div className="bar-segment incomplete" style={{ height: `${height - completionHeight}%` }}></div>
                        </div>
                        {i % 7 === 0 && <span className="bar-label">{new Date(day.date).getDate()}</span>}
                      </div>
                    );
                  })}
                </div>
              )}
              {sessionsByDay.length === 0 && <div className="empty-list">No session data yet</div>}
            </div>
          </div>

          {/* Technique Effectiveness */}
          <div className="analytics-card">
            <h3 className="card-title">Technique Effectiveness</h3>
            <div className="list-container">
              {techniques.slice(0, 6).map((tech, i) => (
                <div key={i} className="list-item technique-item">
                  <span className="list-name">{tech.technique_id}</span>
                  <div className="effectiveness-bar-container">
                    <div
                      className="effectiveness-bar"
                      style={{ width: `${tech.effectivenessRate}%` }}
                    ></div>
                  </div>
                  <span className="list-value">{tech.effectivenessRate}%</span>
                </div>
              ))}
              {techniques.length === 0 && <div className="empty-list">No technique data yet</div>}
            </div>
          </div>

          {/* Zone Tension */}
          <div className="analytics-card">
            <h3 className="card-title">Zones les plus travaillées</h3>
            <div className="list-container">
              {zones.slice(0, 6).map((zone, i) => (
                <div key={i} className="list-item zone-item">
                  <span className="list-rank">{i + 1}</span>
                  <span className="list-name">{formatZoneName(zone.zone)}</span>
                  <div className="zone-stats">
                    <span className="tension-before">{zone.avgBefore}</span>
                    <span className="tension-arrow">→</span>
                    <span className="tension-after">{zone.avgAfter}</span>
                  </div>
                  <span className="list-value reduction">-{zone.avgReduction}</span>
                </div>
              ))}
              {zones.length === 0 && <div className="empty-list">No zone data yet</div>}
            </div>
          </div>

          {/* Trigger Contexts */}
          <div className="analytics-card">
            <h3 className="card-title">Contextes déclencheurs</h3>
            <div className="list-container">
              {triggers.slice(0, 6).map((trigger, i) => (
                <div key={i} className="list-item">
                  <span className="list-name">{formatTriggerContext(trigger.context)}</span>
                  <span className="list-value">{trigger.count}</span>
                </div>
              ))}
              {triggers.length === 0 && <div className="empty-list">No trigger data yet</div>}
            </div>
          </div>
        </div>

        {/* Recent Sessions Table */}
        <div className="analytics-card sessions-card">
          <h3 className="card-title">Recent Sessions</h3>
          <div className="sessions-table-container">
            <table className="sessions-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Context</th>
                  <th>Duration</th>
                  <th>Tension Before</th>
                  <th>Tension After</th>
                  <th>Rating</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {sessions.slice(0, 20).map((session, i) => (
                  <tr key={i}>
                    <td>{new Date(session.started_at).toLocaleDateString()}</td>
                    <td>{session.trigger_context ? formatTriggerContext(session.trigger_context) : '-'}</td>
                    <td>{session.duration_actual ? `${Math.floor(session.duration_actual / 60)}m ${session.duration_actual % 60}s` : '-'}</td>
                    <td className="tension-cell">{session.pre_tension_level || '-'}</td>
                    <td className="tension-cell">{session.post_tension_level || '-'}</td>
                    <td>{session.user_rating ? `${session.user_rating}/5` : '-'}</td>
                    <td>
                      <span className={`status-badge ${session.was_completed ? 'completed' : 'incomplete'}`}>
                        {session.was_completed ? 'Completed' : 'Incomplete'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {sessions.length === 0 && (
              <div className="empty-table">No sessions recorded yet</div>
            )}
          </div>
        </div>

        {/* Favorites Section */}
        <div className="favorites-section">
          <h2 className="section-subtitle">Techniques Favorites</h2>

          {/* Favorites Overview Cards */}
          <div className="stats-overview">
            <div className="stat-card">
              <div className="stat-icon">⭐</div>
              <div className="stat-content">
                <div className="stat-value">{favoritesOverview?.totalFavorites || 0}</div>
                <div className="stat-label">Total Favoris</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">👥</div>
              <div className="stat-content">
                <div className="stat-value">{favoritesOverview?.usersWithFavorites || 0}</div>
                <div className="stat-label">Users avec Favoris</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">🎯</div>
              <div className="stat-content">
                <div className="stat-value">{favoritesOverview?.favoritedTechniques || 0}</div>
                <div className="stat-label">Techniques Favoritées</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">📊</div>
              <div className="stat-content">
                <div className="stat-value">{favoritesOverview?.avgFavoritesPerUser || 0}</div>
                <div className="stat-label">Moy. Favoris/User</div>
              </div>
            </div>
          </div>

          {/* Top Favorited Techniques */}
          <div className="analytics-card">
            <h3 className="card-title">Top Techniques Favorites</h3>
            <div className="sessions-table-container">
              <table className="sessions-table">
                <thead>
                  <tr>
                    <th>Rang</th>
                    <th>Technique</th>
                    <th>Favoris</th>
                    <th>Users</th>
                    <th>Completions</th>
                    <th>Note Moy.</th>
                  </tr>
                </thead>
                <tbody>
                  {topFavorites.map((tech, i) => (
                    <tr
                      key={i}
                      className="clickable-row"
                      onClick={() => setSelectedTechnique(tech)}
                    >
                      <td className="rank-cell">
                        {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}
                      </td>
                      <td className="technique-name">{getTechniqueName(tech.technique_id)}</td>
                      <td>
                        <span className="favorites-badge">
                          ⭐ {tech.favorites_count}
                        </span>
                      </td>
                      <td>{tech.total_users}</td>
                      <td>{tech.total_completions}</td>
                      <td>{tech.avg_rating ? `${tech.avg_rating}/5` : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {topFavorites.length === 0 && (
                <div className="empty-table">Aucune technique n'a encore été ajoutée en favori</div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderFeedbackDashboard = () => {
    const { overview: feedbackOverview, feedbackList } = feedbackData;

    if (feedbackLoading) {
      return <div className="loading">Loading feedback data...</div>;
    }

    return (
      <div className="feedback-dashboard">
        {/* Date Range Selector */}
        <div className="date-range-selector">
          <span className="date-range-label">Time period:</span>
          <div className="date-range-buttons">
            {[7, 14, 30, 90].map(days => (
              <button
                key={days}
                className={`date-range-btn ${dateRange === days ? 'active' : ''}`}
                onClick={() => setDateRange(days)}
              >
                {days} days
              </button>
            ))}
          </div>
        </div>

        {/* Feedback Overview Cards */}
        <div className="stats-overview">
          <div className="stat-card">
            <div className="stat-icon">💬</div>
            <div className="stat-content">
              <div className="stat-value">{feedbackOverview?.totalFeedback || 0}</div>
              <div className="stat-label">Total Retours</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">⭐</div>
            <div className="stat-content">
              <div className="stat-value">{feedbackOverview?.avgRating || 0}/5</div>
              <div className="stat-label">Note Moyenne</div>
            </div>
          </div>
          <div className="stat-card positive">
            <div className="stat-icon">👍</div>
            <div className="stat-content">
              <div className="stat-value">{feedbackOverview?.positivePercentage || 0}%</div>
              <div className="stat-label">Avis Positifs (4-5⭐)</div>
            </div>
          </div>
          <div className="stat-card warning">
            <div className="stat-icon">🔔</div>
            <div className="stat-content">
              <div className="stat-value">{feedbackOverview?.unresolvedCount || 0}</div>
              <div className="stat-label">Non Résolus</div>
            </div>
          </div>
        </div>

        {/* Secondary Stats */}
        <div className="stats-overview secondary">
          <div className="stat-card small">
            <div className="stat-content">
              <div className="stat-value">{feedbackOverview?.positiveCount || 0}</div>
              <div className="stat-label">Avis Positifs</div>
            </div>
          </div>
          <div className="stat-card small negative">
            <div className="stat-content">
              <div className="stat-value">{feedbackOverview?.negativeCount || 0}</div>
              <div className="stat-label">Avis Négatifs</div>
            </div>
          </div>
          <div className="stat-card small">
            <div className="stat-content">
              <div className="stat-value">{Object.keys(feedbackOverview?.byType || {}).length}</div>
              <div className="stat-label">Types de Retours</div>
            </div>
          </div>
        </div>

        {/* Feedback Analytics Grid */}
        <div className="analytics-grid">
          {/* Feedback by Type */}
          <div className="analytics-card">
            <h3 className="card-title">Par Type</h3>
            <div className="list-container">
              {Object.entries(feedbackOverview?.byType || {}).map(([type, count], i) => (
                <div key={i} className="list-item">
                  <span className="feedback-type-badge" data-type={type}>
                    {type === 'rating' ? '⭐ Note' :
                     type === 'bug' ? '🐛 Bug' :
                     type === 'feature' ? '💡 Suggestion' :
                     '💬 Général'}
                  </span>
                  <span className="list-value">{count}</span>
                </div>
              ))}
              {Object.keys(feedbackOverview?.byType || {}).length === 0 && (
                <div className="empty-list">Aucun retour par type</div>
              )}
            </div>
          </div>

          {/* Rating Distribution */}
          <div className="analytics-card">
            <h3 className="card-title">Distribution des Notes</h3>
            <div className="rating-distribution">
              {[5, 4, 3, 2, 1].map(rating => {
                const count = feedbackOverview?.byRating?.[rating] || 0;
                const total = feedbackOverview?.totalFeedback || 1;
                const percentage = Math.round((count / total) * 100) || 0;
                return (
                  <div key={rating} className="rating-bar-row">
                    <span className="rating-label">{'⭐'.repeat(rating)}</span>
                    <div className="rating-bar-container">
                      <div
                        className={`rating-bar ${rating >= 4 ? 'positive' : rating <= 2 ? 'negative' : 'neutral'}`}
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                    <span className="rating-count">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* All Feedback Table */}
        <div className="analytics-card sessions-card">
          <h3 className="card-title">Tous les Retours</h3>
          <div className="sessions-table-container">
            <table className="sessions-table feedback-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Note</th>
                  <th>Commentaire</th>
                  <th>Écran</th>
                  <th>Version</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {feedbackList.map((feedback, i) => (
                  <tr key={i} className={!feedback.is_resolved ? 'unresolved' : ''}>
                    <td>{new Date(feedback.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
                    <td>
                      <span className="feedback-type-badge" data-type={feedback.feedback_type}>
                        {feedback.feedback_type === 'rating' ? '⭐ Note' :
                         feedback.feedback_type === 'bug' ? '🐛 Bug' :
                         feedback.feedback_type === 'feature' ? '💡 Idée' : '💬 Général'}
                      </span>
                    </td>
                    <td>
                      {feedback.rating ? (
                        <span className={`rating-badge ${feedback.rating >= 4 ? 'positive' : feedback.rating <= 2 ? 'negative' : ''}`}>
                          {feedback.rating}/5
                        </span>
                      ) : '-'}
                    </td>
                    <td className="comment-cell" title={feedback.comment}>
                      {feedback.comment || <span className="no-comment">-</span>}
                    </td>
                    <td>{feedback.screen_context || '-'}</td>
                    <td>{feedback.app_version || '-'}</td>
                    <td>
                      <span className={`status-badge ${feedback.is_resolved ? 'completed' : 'incomplete'}`}>
                        {feedback.is_resolved ? 'Résolu' : 'En attente'}
                      </span>
                    </td>
                    <td>
                      {!feedback.is_resolved && (
                        <button
                          className="btn-resolve"
                          onClick={() => handleResolveFeedback(feedback.id)}
                        >
                          ✓ Résoudre
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {feedbackList.length === 0 && (
              <div className="empty-table">Aucun retour utilisateur pour le moment</div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderBlogsPanel = () => (
    <>
      <div className="admin-header">
        <h1 className="admin-title">Blog Management</h1>
        <button
          className="btn-add-blog"
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Cancel' : '+ Add New Blog'}
        </button>
      </div>

      {showForm && (
        <div className="blog-form-container">
          <h2 className="form-title">
            {editingBlog ? 'Edit Blog Post' : 'Create New Blog Post'}
          </h2>
          <form onSubmit={handleSubmit} className="blog-form">
            <div className="form-group">
              <label className="form-label">Title</label>
              <input
                type="text"
                className="form-input"
                value={formData.title}
                onChange={(e) => {
                  const newTitle = e.target.value;
                  setFormData({
                    ...formData,
                    title: newTitle,
                    slug: generateSlug(newTitle)
                  });
                }}
                placeholder="Enter blog title"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">URL Slug (SEO-friendly)</label>
              <input
                type="text"
                className="form-input"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder="auto-generated-from-title"
                required
              />
              <small className="form-helper">This will be used in the URL: /blog/{formData.slug || 'your-slug-here'}</small>
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                className="form-textarea"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Enter blog description"
                rows="4"
                required
              ></textarea>
            </div>

            <div className="form-group">
              <label className="form-label">Content (Markdown)</label>
              <textarea
                className="form-textarea-large"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="Enter blog content using Markdown formatting...&#10;&#10;# Heading 1&#10;## Heading 2&#10;**Bold text**&#10;- List item"
                rows="15"
              ></textarea>
              <small className="form-helper">Use Markdown: # for headings, ** for bold, - for lists</small>
            </div>

            <div className="form-group">
              <label className="form-label">Image</label>
              <div className="image-input-options">
                <div className="image-url-input">
                  <input
                    type="url"
                    className="form-input"
                    value={formData.image_url}
                    onChange={(e) => {
                      setFormData({ ...formData, image_url: e.target.value });
                      setImageFile(null);
                    }}
                    placeholder="Paste image URL here..."
                  />
                </div>
                <div className="image-or-divider">
                  <span>OR</span>
                </div>
                <input
                  type="file"
                  className="form-input-file"
                  onChange={handleImageChange}
                  accept="image/*"
                />
              </div>
              {formData.image_url && (
                <div className="image-preview">
                  <img
                    src={formData.image_url}
                    alt="Preview"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                    onLoad={(e) => {
                      e.target.style.display = 'block';
                      if (e.target.nextSibling) e.target.nextSibling.style.display = 'none';
                    }}
                  />
                  <div className="image-error" style={{ display: 'none' }}>
                    Invalid image URL
                  </div>
                </div>
              )}
            </div>

            <div className="form-actions">
              <button
                type="submit"
                className="btn-submit"
                disabled={uploading}
              >
                {uploading ? 'Uploading...' : editingBlog ? 'Update Blog' : 'Create Blog'}
              </button>
              <button
                type="button"
                className="btn-cancel"
                onClick={resetForm}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="blogs-list">
        <h2 className="section-title">All Blog Posts</h2>
        {isLoading ? (
          <div className="loading">Loading blogs...</div>
        ) : blogs.length === 0 ? (
          <div className="empty-state">No blogs found. Create your first blog post!</div>
        ) : (
          <div className="blogs-grid">
            {blogs.map((blog) => (
              <div key={blog.id} className="blog-item">
                {blog.image_url && (
                  <div className="blog-item-image">
                    <img src={blog.image_url} alt={blog.title} />
                  </div>
                )}
                <div className="blog-item-content">
                  <h3 className="blog-item-title">{blog.title}</h3>
                  <p className="blog-item-desc">{blog.description}</p>
                  <div className="blog-item-actions">
                    <button
                      className="btn-edit"
                      onClick={() => handleEdit(blog)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn-delete"
                      onClick={() => handleDelete(blog.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className="admin-panel">
      <div className="admin-container">
        {/* Navigation Tabs */}
        <div className="admin-tabs">
          <button
            className={`admin-tab ${activeTab === 'analytics' ? 'active' : ''}`}
            onClick={() => setActiveTab('analytics')}
          >
            <span className="tab-icon">📊</span>
            Analytics
          </button>
          <button
            className={`admin-tab ${activeTab === 'seo' ? 'active' : ''}`}
            onClick={() => setActiveTab('seo')}
          >
            <span className="tab-icon">🔍</span>
            SEO
          </button>
          <button
            className={`admin-tab ${activeTab === 'bodyscan' ? 'active' : ''}`}
            onClick={() => setActiveTab('bodyscan')}
          >
            <span className="tab-icon">🧘</span>
            Body Scan
          </button>
          <button
            className={`admin-tab ${activeTab === 'feedback' ? 'active' : ''}`}
            onClick={() => setActiveTab('feedback')}
          >
            <span className="tab-icon">💬</span>
            Feedback
          </button>
          <button
            className={`admin-tab ${activeTab === 'blogs' ? 'active' : ''}`}
            onClick={() => setActiveTab('blogs')}
          >
            <span className="tab-icon">📝</span>
            Blogs
          </button>
          <a href="/" className="admin-tab back-link">
            <span className="tab-icon">←</span>
            Back to Site
          </a>
        </div>

        {/* Content */}
        {activeTab === 'analytics' && (
          <>
            <div className="admin-header">
              <h1 className="admin-title">Analytics Dashboard</h1>
              <button className="btn-refresh" onClick={fetchAnalytics}>
                Refresh
              </button>
            </div>
            {renderAnalyticsDashboard()}
          </>
        )}
        {activeTab === 'seo' && (
          <>
            <div className="admin-header">
              <h1 className="admin-title">SEO Analytics</h1>
              <button className="btn-refresh" onClick={fetchSEOAnalytics}>
                Refresh
              </button>
            </div>
            {renderSEODashboard()}
          </>
        )}
        {activeTab === 'bodyscan' && (
          <>
            <div className="admin-header">
              <h1 className="admin-title">Body Scan Analytics</h1>
              <button className="btn-refresh" onClick={fetchBodyScanData}>
                Refresh
              </button>
            </div>
            {renderBodyScanDashboard()}
          </>
        )}
        {activeTab === 'feedback' && (
          <>
            <div className="admin-header">
              <h1 className="admin-title">Retours Utilisateurs</h1>
              <button className="btn-refresh" onClick={fetchFeedbackData}>
                Refresh
              </button>
            </div>
            {renderFeedbackDashboard()}
          </>
        )}
        {activeTab === 'blogs' && renderBlogsPanel()}
      </div>

      {/* Technique Details Modal */}
      {selectedTechnique && (
        <div className="modal-overlay" onClick={() => setSelectedTechnique(null)}>
          <div className="modal-content technique-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedTechnique(null)}>×</button>
            <h2 className="modal-title">{getTechniqueName(selectedTechnique.technique_id)}</h2>

            <div className="technique-details-grid">
              <div className="detail-card">
                <div className="detail-icon">⭐</div>
                <div className="detail-value">{selectedTechnique.favorites_count}</div>
                <div className="detail-label">Favoris</div>
              </div>
              <div className="detail-card">
                <div className="detail-icon">👥</div>
                <div className="detail-value">{selectedTechnique.total_users}</div>
                <div className="detail-label">Utilisateurs</div>
              </div>
              <div className="detail-card">
                <div className="detail-icon">✅</div>
                <div className="detail-value">{selectedTechnique.total_completions}</div>
                <div className="detail-label">Completions</div>
              </div>
              <div className="detail-card">
                <div className="detail-icon">📊</div>
                <div className="detail-value">{selectedTechnique.avg_rating ? `${selectedTechnique.avg_rating}/5` : '-'}</div>
                <div className="detail-label">Note Moyenne</div>
              </div>
            </div>

            <div className="technique-stats-summary">
              <div className="stat-row">
                <span className="stat-label">Moy. Completions/User</span>
                <span className="stat-value-text">
                  {selectedTechnique.total_users > 0
                    ? (selectedTechnique.total_completions / selectedTechnique.total_users).toFixed(1)
                    : '0'}
                </span>
              </div>
              <div className="stat-row">
                <span className="stat-label">Taux de Favoris</span>
                <span className="stat-value-text">
                  {selectedTechnique.total_users > 0
                    ? Math.round((selectedTechnique.favorites_count / selectedTechnique.total_users) * 100)
                    : 0}%
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
